import type { ZudokuConfig } from 'zudoku';

/**
 * The partner developer portal: the published `/v1` contract, plus the prose a first integration
 * needs.
 *
 * **The document is a FILE, not a URL.** Zudoku will read a spec over HTTP, but it then processes
 * it in the reader's browser on every page load and the site describes whatever production is
 * serving rather than what this tree says. `openapi.json` is written by
 * `pnpm --filter @lux/server openapi` from the same metadata the live route uses, and
 * `pnpm check` fails when it drifts — so a contract change shows up in the diff of the pull
 * request that makes it.
 *
 * **The playground calls production**, cross-origin, which works only because `/v1` allows exactly
 * this origin (`DOCS_ORIGIN` in `packages/api/src/v1/document.ts`). There is no sandbox key: see
 * `pages/authentication.mdx`, which says so where a reader will meet it.
 */

/**
 * The dashboard's palette, mapped onto Zudoku's semantic tokens.
 *
 * `apps/dashboard/tailwind.config.js` says of itself that it IS the look, so the values come from
 * there rather than from a second palette invented here; the Tailwind name is in the comment
 * beside each one. The two vocabularies do not line up one to one — Tailwind scales on one side,
 * shadcn roles on the other — so this mapping is the whole of the translation.
 *
 * The dashboard has no dark theme. Zudoku offers one, and turning it off would be worse than
 * filling it in, so the dark column is the dashboard's own `ink` ramp — the ground its sign-in
 * pages and top bar already use.
 */
const light = {
  background: '#F4F3EE', // canvas
  foreground: '#0D1512', // body
  card: '#FFFFFF',
  cardForeground: '#0D1512', // body
  popover: '#FAF8F1', // cream
  popoverForeground: '#0D1512', // body
  primary: '#0D726D', // leaf.deep — the link and button teal on a light ground
  primaryForeground: '#FAF8F1', // cream
  secondary: '#D6EEEC', // mint
  secondaryForeground: '#0A5652', // leaf.on
  muted: '#F1F1EC', // settled.bg
  mutedForeground: '#5C6763', // body.muted
  accent: '#D6EEEC', // mint
  accentForeground: '#0A5652', // leaf.on
  destructive: '#9B3320', // alarm
  destructiveForeground: '#FCEFEC', // alarm.bg
  border: '#E3E4DF', // rule
  input: '#E3E4DF', // rule
  ring: '#24C2BC', // leaf
};

const dark = {
  background: '#0A1618', // ink
  foreground: '#E3EEF0', // ink.bright
  card: '#122528', // ink.raised
  cardForeground: '#E3EEF0', // ink.bright
  popover: '#122528', // ink.raised
  popoverForeground: '#E3EEF0', // ink.bright
  primary: '#24C2BC', // leaf
  primaryForeground: '#0A1618', // ink
  secondary: '#192A2C', // ink.hover
  secondaryForeground: '#E3EEF0', // ink.bright
  muted: '#122528', // ink.raised
  mutedForeground: '#94AAAE', // ink.dim
  accent: '#192A2C', // ink.hover
  accentForeground: '#6FD0CC', // leaf.bright
  destructive: '#9B3320', // alarm
  destructiveForeground: '#FCEFEC', // alarm.bg
  border: '#1E3539', // ink.line
  input: '#1E3539', // ink.line
  ring: '#6FD0CC', // leaf.bright
};

const config: ZudokuConfig = {
  site: {
    title: 'Lunch',
    /**
     * `lunchMarkWhite.png` is the only mark in the tree, and it is pure white with an alpha
     * channel — invisible on the canvas ground. `lunchMarkInk.png` is that same file with its RGB
     * replaced by `ink` and its alpha kept, so light mode gets a real logo out of the artwork that
     * exists. Replace both the day there is a proper wordmark.
     */
    logo: {
      src: { light: '/lunchMarkInk.png', dark: '/lunchMarkWhite.png' },
      alt: 'Lunch',
      width: '22px',
    },
  },

  metadata: {
    // `%s` is the page's own title, and the landing page's is already "Lunch Partner API" — so the
    // suffix is the company, not the product, or the tab reads "Lunch Partner API — Lunch Partner API".
    title: '%s · Lunch',
    defaultTitle: 'Lunch Partner API',
    description:
      'Finance your vendors’ invoices through Lunch: sync businesses, quote an advance, and take ' +
      'it. REST, versioned, with an OpenAPI document.',
    favicon: '/lunchMarkInk.png',
  },

  theme: {
    light,
    dark,
    /**
     * The same two families the dashboard's `index.html` links, by URL because neither is in
     * Zudoku's predefined list.
     */
    fonts: {
      sans: {
        url: 'https://fonts.googleapis.com/css2?family=Schibsted+Grotesk:wght@400;500;600;700;800&display=swap',
        fontFamily: 'Schibsted Grotesk, system-ui, sans-serif',
      },
      mono: {
        url: 'https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&display=swap',
        fontFamily: 'IBM Plex Mono, ui-monospace, monospace',
      },
    },
  },

  /**
   * The landing page is `pages/index.mdx`, bound to `/` **explicitly**.
   *
   * Naming the file alone gives it the route `/index`, and the failure that follows is a nasty
   * one: the prerendered `index.html` holds the right page and CloudFront serves it, so the first
   * paint is correct — then the router hydrates, finds no route for `/`, and replaces it with
   * "Page not found". A build check cannot see that and neither can a glance at `dist`.
   *
   * A `redirects` entry is not the fix. Those compile into `dist/.output/config.json`, a
   * Vercel-style file CloudFront does not read, so the redirect would simply never happen here.
   */
  navigation: [
    {
      type: 'category',
      label: 'Guides',
      items: [
        { type: 'doc', file: 'index', path: '/', label: 'Overview' },
        'quickstart',
        'authentication',
        'onboarding',
        'financing-an-invoice',
        'external-ids',
        'idempotent-requests',
        'webhooks',
        'errors',
      ],
    },
    { type: 'link', to: '/reference', label: 'API Reference' },
  ],

  apis: [{ type: 'file', input: './openapi.json', path: '/reference' }],

  /**
   * **This list REPLACES the default set, it does not add to it.** Naming `csv` alone — which the
   * bulk import operation's sample needs — silently dropped `shell` and `json`, and every curl
   * block in these guides rendered as plain text. The build says so in a line nobody reads, and
   * the site otherwise looks fine.
   *
   * So: every language any page or any operation description actually uses.
   */
  syntaxHighlighting: { languages: ['bash', 'shell', 'json', 'javascript', 'csv'] },
};

export default config;
